// The look of the sign-in screens, the home screen and each employee's badge: West Coast Wireless's
// brush logo and card in its own store, the sample shop's in the demo, and for every business that
// subscribes its own name (drawn; never West Coast Wireless's logo or card).
import { BADGE_TITLES } from '../../shared/badges';
import type { RoleId } from '../../shared/permissions';

/** The logo is painted on the home screen once per sign-in: who it was last played for. */
export const brandMoment = { home: '' };

/**
 * The public demo shows a made-up shop (Hype City Wireless) instead of the store's own brand: its
 * own wordmark on the sign-in and home screens and a plain card behind every drawn badge. Set
 * once from /api/status when the app starts.
 */
export const brand: { demo: boolean; shopName: string; mode: 'home' | 'demo' | 'business' | 'lobby' } = { demo: false, shopName: '', mode: 'home' };
/** The sample shop's slogan, on its badge card. */
const DEMO_TAGLINE = 'Phones, repairs, no cap.';

/**
 * The brand on the sign-in and home screens: West Coast Wireless's logo in its own store, the
 * sample shop's in the demo, a business's own name for every business that subscribes (never
 * West Coast Wireless's), and Store OS on the shared sign-in screen.
 */
export function BrandLogo({ className = '', animate = false }: { className?: string; animate?: boolean }) {
  if (brand.mode === 'business') return <ShopLogo name={brand.shopName} className={className} animate={animate} />;
  if (brand.demo || brand.mode === 'demo') return <DemoLogo className={className} animate={animate} />;
  if (brand.mode === 'lobby') return <StoreOsLogo className={className} animate={animate} />;
  return <WcwLogo className={className} animate={animate} />;
}

/**
 * The West Coast Wireless logo. With `animate`, the blue lettering is painted on from left to
 * right, WIRELESS rises in under it, and a shine runs across once. Still for reduced motion.
 */
export function WcwLogo({ className = '', animate = false }: { className?: string; animate?: boolean }) {
  return (
    <span className={`wcw${animate ? ' wcw-anim' : ''}${className ? ` ${className}` : ''}`} role="img" aria-label="West Coast Wireless">
      <img className="wcw-word" src="/assets/wcw-logo-word.png" alt="" width={960} height={337} draggable={false} />
      <img className="wcw-brush" src="/assets/wcw-logo-brush.png" alt="" width={960} height={337} draggable={false} />
      {animate ? <span className="wcw-shine" aria-hidden="true" /> : null}
    </span>
  );
}

/** A business's name as its wordmark (drawn, no picture): one or two lines, sized to fit, a blue rule under it. */
export function ShopLogo({ name, className = '', animate = false }: { name: string; className?: string; animate?: boolean }) {
  const text = (name || 'Your shop').replace(/\s+/g, ' ').trim().toUpperCase();
  const lines = splitName(text);
  const longest = Math.max(...lines.map((l) => ems(l)));
  const size = Math.min(132, Math.floor(880 / Math.max(longest, 0.1)));
  const lineH = Math.round(size * 1.08);
  const top = 40 + size;
  const ruleY = top + lineH * (lines.length - 1) + 34;
  const height = ruleY + 30;
  return (
    <span className={`shop-logo${animate ? ' shop-logo-anim' : ''}${className ? ` ${className}` : ''}`} role="img" aria-label={name || 'Your shop'}>
      <svg viewBox={`0 0 960 ${height}`} aria-hidden="true" focusable="false">
        {lines.map((l, i) => (
          <text key={i} className="sl-name" x="480" y={top + i * lineH} textAnchor="middle" fontSize={size} fontWeight="800" fill="#07152D" letterSpacing="2">{l}</text>
        ))}
        <rect className="sl-rule" x="360" y={ruleY} width="240" height="12" rx="6" fill="#0057FF" />
      </svg>
    </span>
  );
}

/** Two lines when a long name splits well, at the space nearest the middle. */
function splitName(text: string): string[] {
  const words = text.split(' ');
  if (words.length < 2 || text.length <= 14) return [text];
  let best = [text];
  let bestW = ems(text);
  for (let i = 1; i < words.length; i++) {
    const a = words.slice(0, i).join(' ');
    const b = words.slice(i).join(' ');
    const w = Math.max(ems(a), ems(b));
    if (w < bestW) { best = [a, b]; bestW = w; }
  }
  return best;
}

/** Store OS on its own sign-in screen (a server with several businesses, nobody signed in yet). */
export function StoreOsLogo({ className = '', animate = false }: { className?: string; animate?: boolean }) {
  return (
    <span className={`sos-logo${animate ? ' shop-logo-anim' : ''}${className ? ` ${className}` : ''}`} role="img" aria-label="Store OS">
      <img src="/assets/store-os-icon-128.png" alt="" width={96} height={96} draggable={false} />
      <span className="sos-logo-t">STORE OS</span>
    </span>
  );
}

/**
 * The sample shop's wordmark (drawn, no picture): the first word of its name on a tilted,
 * sticker-like blue block, the rest letter-spaced under it ("HYPE" / "CITY WIRELESS").
 */
export function DemoLogo({ className = '', animate = false }: { className?: string; animate?: boolean }) {
  const name = (brand.shopName || 'Hype City Wireless').toUpperCase();
  const [first, ...rest] = name.split(' ');
  const tail = rest.length ? rest : ['WIRELESS'];
  const last = tail[tail.length - 1];
  const mid = tail.slice(0, -1).join(' ');
  return (
    <span className={`demo-logo${animate ? ' demo-logo-anim' : ''}${className ? ` ${className}` : ''}`} role="img" aria-label={brand.shopName || 'Hype City Wireless'}>
      <svg viewBox="0 0 960 337" aria-hidden="true" focusable="false">
        <defs>
          <linearGradient id="demo-logo-g" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#0A66FF" />
            <stop offset="1" stopColor="#0033A8" />
          </linearGradient>
        </defs>
        <g className="dl-block" transform="rotate(-3 480 130)">
          <rect x="128" y="52" width="716" height="170" rx="22" fill="#07152D" />
          <rect x="116" y="40" width="716" height="170" rx="22" fill="url(#demo-logo-g)" />
          <rect x="116" y="40" width="716" height="14" rx="7" fill="#00C8E8" />
          <text className="dl-first" x="474" y="177" textAnchor="middle" fontSize="150" fontWeight="900" fontStyle="italic" fill="#07152D" letterSpacing="4" transform="translate(5 5)">{first}</text>
          <text className="dl-first" x="474" y="177" textAnchor="middle" fontSize="150" fontWeight="900" fontStyle="italic" fill="#fff" letterSpacing="4">{first}</text>
        </g>
        <text className="dl-rest" x="480" y="306" textAnchor="middle" fontSize="66" fontWeight="800" letterSpacing="12">
          {mid ? <tspan fill="#07152D">{mid} </tspan> : null}
          <tspan fill="#0057FF">{last}</tspan>
        </text>
      </svg>
    </span>
  );
}

/** Who a badge is for: their picture, or what STORE OS draws one with. */
export interface BadgePerson {
  name: string;
  role?: string;
  role_id?: string;
  badge_url?: string | null;
  badge_name?: string;
  badge_city?: string | null;
}

/** Someone's badge, 2 wide by 3 tall: their own card, or one drawn with their name, job and city. */
export function EmployeeBadge({ person, className = '' }: { person: BadgePerson; className?: string }) {
  return (
    <span className={`ebadge${person.badge_url ? '' : ' drawn'}${className ? ` ${className}` : ''}`}>
      {person.badge_url ? <img src={person.badge_url} alt="" draggable={false} decoding="async" /> : <DrawnBadge person={person} />}
    </span>
  );
}

// How wide each capital is in the badge font (Inter Display ExtraBold), in ems: to size the name so it fits.
const EM: Record<string, number> = {
  A: 0.738, B: 0.647, C: 0.728, D: 0.706, E: 0.619, F: 0.593, G: 0.738, H: 0.722, I: 0.27, J: 0.573, K: 0.706, L: 0.565, M: 0.909,
  N: 0.731, O: 0.747, P: 0.639, Q: 0.747, R: 0.658, S: 0.668, T: 0.645, U: 0.705, V: 0.727, W: 1.016, X: 0.718, Y: 0.709, Z: 0.643,
  '0': 0.665, '1': 0.401, '2': 0.614, '3': 0.628, '4': 0.673, '5': 0.608, '6': 0.631, '7': 0.564, '8': 0.627, '9': 0.631,
  ' ': 0.2, '.': 0.254, ',': 0.254, "'": 0.278, '-': 0.452, '&': 0.663,
};
const ems = (s: string) => [...s].reduce((w, ch) => w + (EM[ch] ?? 0.72), 0);

/** The text on a drawn badge, sized so it fits the card (600 x 900). */
export function badgeText(p: BadgePerson) {
  const name = (p.badge_name || p.name.split(' ')[0] || '').toUpperCase();
  const job = (BADGE_TITLES[p.role_id as RoleId] ?? p.role ?? '').toUpperCase();
  const city = (p.badge_city ?? '').toUpperCase();
  const nameSize = Math.min(150, Math.floor(540 / Math.max(ems(name), 0.1)));
  // Letter spacing adds to each letter's width; the role and city sit in one line.
  const jobSpace = 0.3;
  const jobSize = Math.min(24, Math.floor(540 / Math.max(ems(job) + jobSpace * job.length, 0.1)));
  const citySpace = 0.42;
  const citySize = Math.min(17, Math.floor(400 / Math.max(ems(city) + citySpace * city.length, 0.1)));
  const cityWidth = (ems(city) + citySpace * city.length) * citySize;
  return { name, job, city, nameSize, jobSize, jobSpace, citySize, citySpace, cityWidth };
}

/** The demo's badge card: the sample shop's name on white, a blue field for the person's name. No store's own picture. */
function DemoCard() {
  const words = (brand.shopName || 'Hype City Wireless').toUpperCase().split(' ');
  const first = words[0];
  const rest = words.slice(1).join(' ') || 'WIRELESS';
  const tagline = (DEMO_TAGLINE || '').toUpperCase().replace(/[.,]+\s*/g, ' · ').replace(/\s*·\s*$/, '').trim();
  return (
    <g aria-hidden="true">
      <rect x="0" y="0" width="600" height="900" fill="#fff" />
      <rect x="0" y="560" width="600" height="340" fill="#0A3DBB" />
      <rect x="0" y="560" width="600" height="8" fill="#00C8E8" />
      <g transform="rotate(-3 300 160)">
        <rect x="76" y="88" width="460" height="150" rx="18" fill="#07152D" />
        <rect x="66" y="78" width="460" height="150" rx="18" fill="#0A66FF" />
        <rect x="66" y="78" width="460" height="12" rx="6" fill="#00C8E8" />
        <text x="296" y="198" textAnchor="middle" fontFamily="'Badge Display', 'DejaVu Sans', Arial, sans-serif" fontSize="118" fontWeight="900" fontStyle="italic" fill="#fff" letterSpacing="3">{first}</text>
      </g>
      <text x="300" y="312" textAnchor="middle" fontFamily="'Badge Display', 'DejaVu Sans', Arial, sans-serif" fontSize="48" fontWeight="800" fill="#07152D" letterSpacing="9">{rest}</text>
      <path d="M150 352 H450" stroke="#00C8E8" strokeWidth="6" strokeLinecap="round" />
      <text x="300" y="428" textAnchor="middle" fontFamily="'DejaVu Sans', Arial, sans-serif" fontSize="26" fontWeight="700" fill="#52647B" letterSpacing="3">PHONES · TABLETS · LAPTOPS</text>
      <text x="300" y="474" textAnchor="middle" fontFamily="'DejaVu Sans', Arial, sans-serif" fontSize="26" fontWeight="700" fill="#52647B" letterSpacing="3">GAME CONSOLES</text>
      <text x="300" y="530" textAnchor="middle" fontFamily="'DejaVu Sans', Arial, sans-serif" fontSize="25" fontWeight="800" fill="#0057FF" letterSpacing="3">{tagline || 'PHONES · REPAIRS · NO CAP'}</text>
    </g>
  );
}

/** A subscribing business's badge card: its name on white, a blue field for the person's name. */
function ShopCard() {
  const lines = splitName((brand.shopName || 'Your shop').replace(/\s+/g, ' ').trim().toUpperCase());
  const size = Math.min(92, Math.floor(520 / Math.max(...lines.map((l) => ems(l)), 0.1)));
  const top = lines.length === 1 ? 300 : 250;
  return (
    <g aria-hidden="true">
      <rect x="0" y="0" width="600" height="900" fill="#fff" />
      <rect x="0" y="560" width="600" height="340" fill="#0A3DBB" />
      <rect x="0" y="560" width="600" height="8" fill="#0057FF" />
      {lines.map((l, i) => (
        <text key={i} x="300" y={top + i * Math.round(size * 1.1)} textAnchor="middle" fontFamily="'Badge Display', 'DejaVu Sans', Arial, sans-serif" fontSize={size} fontWeight="800" fill="#07152D" letterSpacing="2">{l}</text>
      ))}
      <path d="M210 420 H390" stroke="#0057FF" strokeWidth="8" strokeLinecap="round" />
    </g>
  );
}

function DrawnBadge({ person }: { person: BadgePerson }) {
  const t = badgeText(person);
  return (
    <svg viewBox="0 0 600 900" aria-hidden="true" focusable="false">
      {brand.mode === 'business' ? <ShopCard /> : brand.demo ? <DemoCard /> : <image href="/assets/badge-template.jpg" x="0" y="0" width="600" height="900" preserveAspectRatio="none" />}
      <text className="eb-name" x="300" y="704" textAnchor="middle" fontSize={t.nameSize}>{t.name}</text>
      {/* letter-spacing leaves a gap after the last letter: half of it moves the line back to the middle. */}
      {t.job ? <text className="eb-job" x={300 + (t.jobSpace * t.jobSize) / 2} y="744" textAnchor="middle" fontSize={t.jobSize} letterSpacing={`${t.jobSpace}em`}>{t.job}</text> : null}
      {t.city ? (
        <>
          <text className="eb-city" x={300 + (t.citySpace * t.citySize) / 2} y="881" textAnchor="middle" fontSize={t.citySize} letterSpacing={`${t.citySpace}em`}>{t.city}</text>
          <line className="eb-rule" x1={300 - t.cityWidth / 2 - 92} x2={300 - t.cityWidth / 2 - 20} y1="875" y2="875" />
          <line className="eb-rule" x1={300 + t.cityWidth / 2 + 20} x2={300 + t.cityWidth / 2 + 92} y1="875" y2="875" />
        </>
      ) : null}
    </svg>
  );
}
